export type GeoHit = {
  /** Human-readable label for the closest known place. */
  label: string;
  /** Distance in km from the query point to the matched place. */
  distanceKm: number;
  /** True when the hit is the user's saved home (very close). */
  isHome: boolean;
};

type City = { name: string; admin: string; country: string; lat: number; lng: number };

// Tiny offline corpus: a few dozen anchor points covering most populated regions.
// The point isn't precision — it's giving the user an "ah, this leaks my city" gut check.
const CITIES: readonly City[] = [
  { name: 'New York', admin: 'NY', country: 'US', lat: 40.7128, lng: -74.006 },
  { name: 'Brooklyn', admin: 'NY', country: 'US', lat: 40.6782, lng: -73.9442 },
  { name: 'Boston', admin: 'MA', country: 'US', lat: 42.3601, lng: -71.0589 },
  { name: 'Washington', admin: 'DC', country: 'US', lat: 38.9072, lng: -77.0369 },
  { name: 'Bethesda', admin: 'MD', country: 'US', lat: 38.9847, lng: -77.0947 },
  { name: 'Philadelphia', admin: 'PA', country: 'US', lat: 39.9526, lng: -75.1652 },
  { name: 'Atlanta', admin: 'GA', country: 'US', lat: 33.749, lng: -84.388 },
  { name: 'Miami', admin: 'FL', country: 'US', lat: 25.7617, lng: -80.1918 },
  { name: 'Chicago', admin: 'IL', country: 'US', lat: 41.8781, lng: -87.6298 },
  { name: 'Detroit', admin: 'MI', country: 'US', lat: 42.3314, lng: -83.0458 },
  { name: 'Minneapolis', admin: 'MN', country: 'US', lat: 44.9778, lng: -93.265 },
  { name: 'Dallas', admin: 'TX', country: 'US', lat: 32.7767, lng: -96.797 },
  { name: 'Houston', admin: 'TX', country: 'US', lat: 29.7604, lng: -95.3698 },
  { name: 'Austin', admin: 'TX', country: 'US', lat: 30.2672, lng: -97.7431 },
  { name: 'Denver', admin: 'CO', country: 'US', lat: 39.7392, lng: -104.9903 },
  { name: 'Phoenix', admin: 'AZ', country: 'US', lat: 33.4484, lng: -112.074 },
  { name: 'Las Vegas', admin: 'NV', country: 'US', lat: 36.1699, lng: -115.1398 },
  { name: 'Los Angeles', admin: 'CA', country: 'US', lat: 34.0522, lng: -118.2437 },
  { name: 'San Diego', admin: 'CA', country: 'US', lat: 32.7157, lng: -117.1611 },
  { name: 'San Francisco', admin: 'CA', country: 'US', lat: 37.7749, lng: -122.4194 },
  { name: 'Oakland', admin: 'CA', country: 'US', lat: 37.8044, lng: -122.2712 },
  { name: 'Seattle', admin: 'WA', country: 'US', lat: 47.6062, lng: -122.3321 },
  { name: 'Portland', admin: 'OR', country: 'US', lat: 45.5152, lng: -122.6784 },
  { name: 'Vancouver', admin: 'BC', country: 'CA', lat: 49.2827, lng: -123.1207 },
  { name: 'Toronto', admin: 'ON', country: 'CA', lat: 43.6532, lng: -79.3832 },
  { name: 'Montreal', admin: 'QC', country: 'CA', lat: 45.5017, lng: -73.5673 },
  { name: 'Mexico City', admin: 'CDMX', country: 'MX', lat: 19.4326, lng: -99.1332 },
  { name: 'London', admin: '', country: 'UK', lat: 51.5074, lng: -0.1278 },
  { name: 'Paris', admin: '', country: 'FR', lat: 48.8566, lng: 2.3522 },
  { name: 'Berlin', admin: '', country: 'DE', lat: 52.52, lng: 13.405 },
  { name: 'Amsterdam', admin: '', country: 'NL', lat: 52.3676, lng: 4.9041 },
  { name: 'Dublin', admin: '', country: 'IE', lat: 53.3498, lng: -6.2603 },
  { name: 'Madrid', admin: '', country: 'ES', lat: 40.4168, lng: -3.7038 },
  { name: 'Barcelona', admin: '', country: 'ES', lat: 41.3851, lng: 2.1734 },
  { name: 'Rome', admin: '', country: 'IT', lat: 41.9028, lng: 12.4964 },
  { name: 'Stockholm', admin: '', country: 'SE', lat: 59.3293, lng: 18.0686 },
  { name: 'Copenhagen', admin: '', country: 'DK', lat: 55.6761, lng: 12.5683 },
  { name: 'Tokyo', admin: '', country: 'JP', lat: 35.6762, lng: 139.6503 },
  { name: 'Seoul', admin: '', country: 'KR', lat: 37.5665, lng: 126.978 },
  { name: 'Singapore', admin: '', country: 'SG', lat: 1.3521, lng: 103.8198 },
  { name: 'Hong Kong', admin: '', country: 'HK', lat: 22.3193, lng: 114.1694 },
  { name: 'Sydney', admin: 'NSW', country: 'AU', lat: -33.8688, lng: 151.2093 },
  { name: 'Melbourne', admin: 'VIC', country: 'AU', lat: -37.8136, lng: 144.9631 },
  { name: 'São Paulo', admin: 'SP', country: 'BR', lat: -23.5505, lng: -46.6333 },
  { name: 'Buenos Aires', admin: '', country: 'AR', lat: -34.6037, lng: -58.3816 },
];

const HOME_KEY = 'anomia:home-coords';
/** Distance threshold (km) below which we describe a hit as "near home". */
const HOME_RADIUS_KM = 5;

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance via the haversine formula. */
export const haversineKm = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number => {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const formatCity = (c: City): string => {
  if (c.admin) return `${c.name}, ${c.admin}`;
  return c.name;
};

const readHome = (): { lat: number; lng: number } | null => {
  try {
    const raw = localStorage.getItem(HOME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: unknown; lng?: unknown };
    if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
      return { lat: parsed.lat, lng: parsed.lng };
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const setHome = (lat: number, lng: number): void => {
  try {
    localStorage.setItem(HOME_KEY, JSON.stringify({ lat, lng }));
  } catch {
    /* localStorage may be unavailable — fail silently */
  }
};

export const reverseGeocode = (lat: number, lng: number): GeoHit | null => {
  const point = { lat, lng };
  const home = readHome();
  if (home) {
    const homeDist = haversineKm(home, point);
    if (homeDist <= HOME_RADIUS_KM) {
      return { label: 'near home', distanceKm: homeDist, isHome: true };
    }
  }

  let nearest: City | null = null;
  let nearestDist = Infinity;
  for (const c of CITIES) {
    const d = haversineKm(c, point);
    if (d < nearestDist) {
      nearest = c;
      nearestDist = d;
    }
  }
  if (!nearest) return null;
  return { label: `near ${formatCity(nearest)}`, distanceKm: nearestDist, isHome: false };
};
